import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import { useLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-line-items-list-sections";
import useLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-line-items-quantities-summary";
import {
  useNotTreatedByMerchant,
  useSetNotTreatedByMerchant,
} from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context";
import { useRefundLineItem } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-context";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import { ArticleItem } from "../article-item/article-item";
import useTranslation from "@babackapp/translations/use-translation";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import useGetItemsInActiveReturns from "@babackapp/private-api-client/client/use-get-items-in-active-returns";
import { useConflictingItems } from "@/pages/(app)/returns/hooks/use-conflicting-items.tsx";
import { ConflictWarning } from "@/pages/(app)/returns/components/conflict-warning";

export default function ArticlesToHandleByMerchant() {
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const { itemsToShowToHandle } = useLineItemsListSections();
  const isAdmin = useViewerHasAtLeastAdminRights();
  const summary = useLineItemsQuantitiesSummary({
    state: "not-treated-by-merchant",
  });
  const t = useTranslation();

  const {
    data: itemsInActiveReturns,
  } = useGetItemsInActiveReturns({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
  });

  if (!itemsToShowToHandle.length || !isAdmin) return null;

  return (
    <AccordionItem value="toHandleByMerchant">
      <AccordionTrigger>
        <span>
          {t("returnDetail.confirmModal.toTreat")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsToShowToHandle.map((lineItem, index) => (
            <ToHandleByMerchantArticle
              key={index}
              lineItem={lineItem}
              itemsInActiveReturns={itemsInActiveReturns}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function ToHandleByMerchantArticle({
  lineItem,
  itemsInActiveReturns,
}: {
  lineItem: ReturnRequestLineItemDto;
  itemsInActiveReturns?: { itemsInOtherReturns: Array<{ lineItemId: string; quantity: number; returnRequestId: string; returnRequestName: string; }> };
}) {
  const { data: shop } = useCurrentShopSuspense();
  const { item, updateResolution, updateVariant } = useRefundLineItem(lineItem);
  const quantityValue = useNotTreatedByMerchant(lineItem.line_item_id);
  const updateQuantity = useSetNotTreatedByMerchant(lineItem.line_item_id);

  const {
    conflictingItems,
    adjustedMaxQuantity,
    isDisabled,
    warningMessage,
  } = useConflictingItems({
    lineItemId: lineItem.line_item_id,
    itemsInActiveReturns: itemsInActiveReturns?.itemsInOtherReturns,
    currentQuantity: quantityValue.selectedQuantity,
    maxQuantity: quantityValue.maxQuantity,
  });

  const hasConflict = conflictingItems.length > 0;

  // Get unique returns for the component
  const uniqueReturns = Array.from(
    new Map(conflictingItems.map(item => [item.returnRequestId, item])).values()
  );

  const displayMessage = hasConflict && warningMessage ? (
    <ConflictWarning
      warningMessage={warningMessage}
      conflictingReturns={uniqueReturns}
      adjustedMaxQuantity={adjustedMaxQuantity}
      shopId={shop.id}
    />
  ) : undefined;

  return (
    <ArticleItem
      lineItem={item}
      quantity={quantityValue.selectedQuantity}
      maxQuantity={adjustedMaxQuantity}
      onChangeQuantity={updateQuantity}
      onChangeVariant={updateVariant}
      onChangeResolution={updateResolution}
      hideStatus
      disabled={isDisabled}
      disabledReason={displayMessage}
    />
  );
}
